import { useState, useMemo, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { useTranslation } from "react-i18next";
import { Maximize2, X } from "lucide-react";
import { getGeoTrafficForPlanId } from "../../../data/dashboard-projects-dummy";
import iso3166A3ToA2 from "../../../data/iso3166-alpha3-to-alpha2.json";
import styles from "./DashboardGeoMap.module.css";

const GEO_URL = "https://raw.githubusercontent.com/holtzy/D3-graph-gallery/master/DATA/world.geojson";

const ALPHA2_TO_ALPHA3 = Object.fromEntries(
  Object.entries(iso3166A3ToA2).map(([a3, a2]) => [
    String(a2).toUpperCase(),
    a3,
  ])
);

function isValidIsoA3(code) {
  const c = String(code ?? "")
    .trim()
    .toUpperCase();
  return /^[A-Z]{3}$/.test(c) && c !== "-99";
}

function resolveIso3FromGeo(geo) {
  const p = geo?.properties || {};
  if (isValidIsoA3(geo?.id)) return String(geo.id).trim().toUpperCase();
  if (isValidIsoA3(p.ISO_A3)) return String(p.ISO_A3).trim().toUpperCase();
  if (isValidIsoA3(p.ADM0_A3)) return String(p.ADM0_A3).trim().toUpperCase();
  if (isValidIsoA3(p.WB_A3)) return String(p.WB_A3).trim().toUpperCase();
  const a2 = String(p.ISO_A2 ?? "")
    .trim()
    .toUpperCase();
  if (/^[A-Z]{2}$/.test(a2) && ALPHA2_TO_ALPHA3[a2]) {
    return ALPHA2_TO_ALPHA3[a2];
  }
  return "";
}

const MAP_VIEWS = {
  worldwide: {
    projection: "geoEqualEarth",
    projectionConfig: { scale: 128 },
  },
  europe: {
    projection: "geoMercator",
    projectionConfig: {
      scale: 380,
      center: [17, 51],
    },
  },
};

const CARD_MAP_W = 640;
const CARD_MAP_H = 320;

function getProjectionForMapWidth(mapView, mapWidth) {
  const base = MAP_VIEWS[mapView] || MAP_VIEWS.europe;
  const factor = mapWidth / CARD_MAP_W;
  const projectionConfig = { ...base.projectionConfig };
  if (base.projectionConfig.scale != null) {
    projectionConfig.scale = Math.round(base.projectionConfig.scale * factor);
  }
  return {
    projection: base.projection,
    projectionConfig,
  };
}

function useModalMapDims(expanded) {
  const [dims, setDims] = useState({ w: 1000, h: 500 });
  useLayoutEffect(() => {
    if (!expanded) return;
    const update = () => {
      const w = Math.min(1100, Math.max(480, window.innerWidth - 64));
      const h = Math.round(w * (CARD_MAP_H / CARD_MAP_W));
      setDims({ w, h });
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [expanded]);
  return dims;
}

function colorForValue(value, minV, maxV) {
  if (value == null || Number.isNaN(value)) return "#e8edf3";
  const t = Math.max(0, Math.min(1, (value - minV) / (maxV - minV || 1)));
  const a = [247, 252, 240];
  const b = [21, 128, 61];
  const r = Math.round(a[0] + (b[0] - a[0]) * t);
  const g = Math.round(a[1] + (b[1] - a[1]) * t);
  const bl = Math.round(a[2] + (b[2] - a[2]) * t);
  return `rgb(${r},${g},${bl})`;
}

const geoDefaultStyle = {
  outline: "none",
  transition: "fill 0.55s cubic-bezier(0.4, 0, 0.2, 1)",
};

const geoPressedStyle = { outline: "none" };

function getGeoHoverStyle(hasData) {
  return {
    outline: "none",
    fill: hasData ? "#86efac" : "#cbd5e1",
    transition: "fill 0.2s ease",
  };
}

function formatSharePct(p) {
  const x = Number(p);
  if (Number.isNaN(x) || x <= 0) return "0%";
  if (x < 0.05) return "<1%";
  if (x >= 99.95) return `${Math.round(x)}%`;
  return `${Math.round(x * 10) / 10}%`;
}

function buildPctMapsFromRaw(raw) {
  const entries = Object.entries(raw || {});
  const total = entries.reduce(
    (s, [, v]) => s + (Number(v) || 0),
    0
  );
  const pct = {};
  for (const [k, v] of entries) {
    const c = Number(v) || 0;
    pct[k] = total > 0 ? (c / total) * 100 : 0;
  }
  return { total, pct, raw };
}

function buildMapsFromGscCountries(countries) {
  const raw = {};
  let total = 0;
  for (const c of countries || []) {
    const code = String(c.code || "").toUpperCase();
    if (!code) continue;
    const clicks = Number(c.clicks) || 0;
    total += clicks;
    raw[code] = clicks;
  }
  const pct = {};
  for (const c of countries || []) {
    const code = String(c.code || "").toUpperCase();
    if (!code) continue;
    const sh = c.share;
    if (sh != null && Number.isFinite(Number(sh))) {
      pct[code] = Number(sh);
    } else {
      const clicks = raw[code] || 0;
      pct[code] = total > 0 ? (clicks / total) * 100 : 0;
    }
  }
  return { total, pct, raw };
}

function GeoMapSection({
  mapView,
  setMapView,
  geoScale,
  hover,
  setHover,
  mapWidth,
  mapHeight,
  mapWrapExtraClass = "",
  svgExtraClass = "",
  useLiveGsc,
  t,
}) {
  const mapProjection = getProjectionForMapWidth(mapView, mapWidth);

  return (
    <>
      <div
        className={styles.mapTabs}
        role="tablist"
        aria-label={t("analytics.geoMapTabsAria")}
      >
        <button
          type="button"
          role="tab"
          id="geo-map-tab-europe"
          aria-selected={mapView === "europe"}
          aria-controls="geo-map-panel"
          className={`${styles.mapTab} ${
            mapView === "europe" ? styles.mapTabActive : ""
          }`}
          onClick={() => setMapView("europe")}
        >
          {t("analytics.geoMapTabEurope")}
        </button>
        <button
          type="button"
          role="tab"
          id="geo-map-tab-worldwide"
          aria-selected={mapView === "worldwide"}
          aria-controls="geo-map-panel"
          className={`${styles.mapTab} ${
            mapView === "worldwide" ? styles.mapTabActive : ""
          }`}
          onClick={() => setMapView("worldwide")}
        >
          {t("analytics.geoMapTabWorldwide")}
        </button>
      </div>

      <div
        className={`${styles.mapWrap} ${mapWrapExtraClass}`.trim()}
        id="geo-map-panel"
        role="tabpanel"
        aria-labelledby={
          mapView === "europe" ? "geo-map-tab-europe" : "geo-map-tab-worldwide"
        }
      >
        <ComposableMap
          key={`${mapView}-${mapWidth}`}
          projection={mapProjection.projection}
          projectionConfig={mapProjection.projectionConfig}
          width={mapWidth}
          height={mapHeight}
          className={`${styles.svg} ${svgExtraClass}`.trim()}
        >
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const name = geo.properties.name;
                const iso3Key =
                  geoScale.mode === "live" ? resolveIso3FromGeo(geo) : "";
                const visits =
                  geoScale.mode === "live"
                    ? iso3Key
                      ? geoScale.raw[iso3Key]
                      : undefined
                    : geoScale.raw[name];
                const sharePct =
                  geoScale.mode === "live"
                    ? iso3Key
                      ? geoScale.pct[iso3Key]
                      : undefined
                    : geoScale.pct[name];
                const fill = colorForValue(
                  sharePct != null && visits != null ? sharePct : null,
                  geoScale.minV,
                  geoScale.maxV
                );
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={fill}
                    stroke="#94a3b8"
                    strokeWidth={0.35}
                    style={{
                      default: geoDefaultStyle,
                      hover: getGeoHoverStyle(visits != null),
                      pressed: geoPressedStyle,
                    }}
                    onMouseEnter={() =>
                      setHover({
                        name,
                        visits,
                        sharePct:
                          sharePct != null && visits != null
                            ? sharePct
                            : null,
                      })
                    }
                    onMouseLeave={() => setHover(null)}
                  />
                );
              })
            }
          </Geographies>
        </ComposableMap>
      </div>

      <div className={styles.legend}>
        <span className={styles.legendText}>
          {t("analytics.geoMapLegendLow")}
        </span>
        <div className={styles.legendBar} aria-hidden />
        <span className={styles.legendText}>
          {t("analytics.geoMapLegendHigh")}
        </span>
        <span className={styles.legendUnit}>
          (
          {useLiveGsc
            ? t("analytics.geoMapLegendUnitGscShare")
            : t("analytics.geoMapLegendUnitShare")}
          )
        </span>
      </div>

      <div className={styles.hoverArea}>
        {hover ? (
          <div className={styles.hoverPanel}>
            <div className={styles.hoverCountry}>{hover.name}</div>
            {hover.visits != null ? (
              <>
                <div className={styles.hoverValue}>
                  {hover.sharePct != null
                    ? formatSharePct(hover.sharePct)
                    : hover.visits.toLocaleString()}
                </div>
                <div className={styles.hoverMetric}>
                  {hover.sharePct != null
                    ? t("analytics.geoMapTrafficMetricShare")
                    : useLiveGsc
                      ? t("analytics.geoMapTrafficMetricGsc")
                      : t("analytics.geoMapTrafficMetric")}
                </div>
                {hover.sharePct != null ? (
                  <div className={styles.hoverClicksLine}>
                    {useLiveGsc
                      ? t("analytics.geoMapHoverClicksCount", {
                          count: hover.visits.toLocaleString(),
                        })
                      : t("analytics.geoMapHoverVisitsCount", {
                          count: hover.visits.toLocaleString(),
                        })}
                  </div>
                ) : null}
              </>
            ) : (
              <div className={styles.hoverNoData}>
                {t(
                  useLiveGsc
                    ? "analytics.geoMapNoDataGsc"
                    : "analytics.geoMapNoData"
                )}
              </div>
            )}
          </div>
        ) : (
          <p className={styles.hoverHint}>
            {t(
              useLiveGsc
                ? "analytics.geoMapHoverHintGsc"
                : "analytics.geoMapHoverHint"
            )}
          </p>
        )}
      </div>
    </>
  );
}

export default function DashboardGeoMap({
  projectId,
  fillColumn = false,
  liveTrafficByIso3 = null,
  liveGscCountries = null,
  useLiveGsc = false,
}) {
  const { t } = useTranslation("dashboard");
  const [hover, setHover] = useState(null);
  const [mapView, setMapView] = useState("europe");
  const [mapExpanded, setMapExpanded] = useState(false);
  const modalDims = useModalMapDims(mapExpanded);

  useLayoutEffect(() => {
    if (!mapExpanded) return;
    const onKey = (e) => {
      if (e.key === "Escape") setMapExpanded(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mapExpanded]);

  useLayoutEffect(() => {
    if (!mapExpanded) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mapExpanded]);

  const geoScale = useMemo(() => {
    if (useLiveGsc) {
      if (liveGscCountries?.length) {
        const { total, pct, raw } = buildMapsFromGscCountries(liveGscCountries);
        const keys = Object.keys(raw);
        if (!keys.length) {
          return {
            mode: "live",
            raw: {},
            pct: {},
            total: 0,
            minV: 0,
            maxV: 100,
          };
        }
        return {
          mode: "live",
          raw,
          pct,
          total,
          minV: 0,
          maxV: 100,
        };
      }
      const raw =
        liveTrafficByIso3 && typeof liveTrafficByIso3 === "object"
          ? liveTrafficByIso3
          : {};
      const keys = Object.keys(raw);
      if (!keys.length) {
        return {
          mode: "live",
          raw: {},
          pct: {},
          total: 0,
          minV: 0,
          maxV: 100,
        };
      }
      const { total, pct, raw: rawOut } = buildPctMapsFromRaw(raw);
      return {
        mode: "live",
        raw: rawOut,
        pct,
        total,
        minV: 0,
        maxV: 100,
      };
    }
    const trafficByCountry = getGeoTrafficForPlanId(projectId);
    const { total, pct, raw } = buildPctMapsFromRaw(trafficByCountry);
    return {
      mode: "dummy",
      raw,
      pct,
      total,
      minV: 0,
      maxV: 100,
    };
  }, [projectId, useLiveGsc, liveTrafficByIso3, liveGscCountries]);

  return (
    <div className={`${styles.wrap} ${fillColumn ? styles.wrapFillColumn : ""}`}>
      <div className={styles.card}>
        <div className={styles.titleRow}>
          <h3 className={styles.title}>{t("analytics.geoMapTitle")}</h3>
          <button
            type="button"
            className={styles.expandButton}
            onClick={() => setMapExpanded(true)}
            disabled={mapExpanded}
            aria-label={t("analytics.geoMapExpand")}
            aria-expanded={mapExpanded}
            aria-haspopup="dialog"
          >
            <Maximize2 size={18} strokeWidth={2} aria-hidden />
          </button>
        </div>

        {!mapExpanded ? (
          <GeoMapSection
            mapView={mapView}
            setMapView={setMapView}
            geoScale={geoScale}
            hover={hover}
            setHover={setHover}
            mapWidth={CARD_MAP_W}
            mapHeight={CARD_MAP_H}
            useLiveGsc={useLiveGsc}
            t={t}
          />
        ) : null}
      </div>

      {mapExpanded
        ? createPortal(
            <div
              className={styles.modalBackdrop}
              onClick={() => setMapExpanded(false)}
              role="presentation"
            >
              <div
                className={styles.modalPanel}
                role="dialog"
                aria-modal="true"
                aria-labelledby="geo-map-modal-title"
                onClick={(e) => e.stopPropagation()}
              >
                <div className={styles.modalHeader}>
                  <h2 id="geo-map-modal-title" className={styles.modalTitle}>
                    {t("analytics.geoMapModalTitle")}
                  </h2>
                  <button
                    type="button"
                    className={styles.modalClose}
                    onClick={() => setMapExpanded(false)}
                    aria-label={t("analytics.geoMapModalClose")}
                  >
                    <X size={22} strokeWidth={2} aria-hidden />
                  </button>
                </div>
                <GeoMapSection
                  mapView={mapView}
                  setMapView={setMapView}
                  geoScale={geoScale}
                  hover={hover}
                  setHover={setHover}
                  mapWidth={modalDims.w}
                  mapHeight={modalDims.h}
                  mapWrapExtraClass={styles.modalMapWrap}
                  svgExtraClass={styles.modalMapSvg}
                  useLiveGsc={useLiveGsc}
                  t={t}
                />
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
